/**
 * Sanitize legacy editorial body HTML to a rich but safe whitelist — headings,
 * lists, links, images, quotes and tables survive; every class/style/id/event
 * handler, Drupal `<?php ?>` fragment, `<script>` and `<iframe>` is stripped.
 *
 * Used by the RichText block to render ~178 migrated editorial pages whose
 * bodies the minimal paragraph sanitiser (`paragraphHtml.ts`) would destroy.
 *
 * Dependency-light by design — a regex pass in the same spirit as
 * `paragraphHtml.ts`. The sanitiser is the ONLY path body HTML reaches the DOM.
 */

/** Tags kept in the output. Everything else has its tag markup removed. */
const ALLOWED_TAGS = new Set([
  "p",
  "h2",
  "h3",
  "h4",
  "ul",
  "ol",
  "li",
  "a",
  "img",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "br",
  "blockquote",
  "table",
  "thead",
  "tbody",
  "tr",
  "th",
  "td",
  "hr",
  "span",
  "div",
  "sub",
  "sup",
]);

/** Void tags that never carry a closing tag. */
const VOID_TAGS = new Set(["img", "br", "hr"]);

/** Tags whose entire content (not just the tag) must be discarded. */
const DROP_CONTENT_TAGS = new Set([
  "script",
  "style",
  "iframe",
  "object",
  "embed",
  "noscript",
  "template",
  "head",
  "title",
]);

/**
 * Pulls a single attribute value out of a raw tag's attribute string.
 * Matches `name="…"`, `name='…'` and bare `name=value` forms.
 */
function readAttr(attrs: string, name: string): string | null {
  const re = new RegExp(
    `\\b${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s"'>]+))`,
    "i",
  );
  const m = re.exec(attrs);
  if (!m) return null;
  return m[1] ?? m[2] ?? m[3] ?? "";
}

/** True when an href is safe to keep (no javascript:/vbscript:/data: scheme). */
function isSafeHref(href: string): boolean {
  const v = href.trim().toLowerCase();
  // Allow protocol-relative, absolute, anchor, mailto, tel and relative URLs.
  // eslint-disable-next-line no-control-regex
  const stripped = v.replace(/[ - ]+/g, "");
  return !/^(?:javascript|vbscript|data):/i.test(stripped);
}

/** Builds the sanitised attribute string for a whitelisted tag. */
function safeAttributes(tag: string, rawAttrs: string): string {
  const parts: string[] = [];
  if (tag === "a") {
    const href = readAttr(rawAttrs, "href");
    if (href != null && isSafeHref(href)) {
      parts.push(`href="${escapeAttr(href.trim())}"`);
    }
  } else if (tag === "img") {
    const src = readAttr(rawAttrs, "src");
    if (src != null) parts.push(`src="${escapeAttr(src.trim())}"`);
    const alt = readAttr(rawAttrs, "alt");
    if (alt != null) parts.push(`alt="${escapeAttr(alt.trim())}"`);
  } else if (tag === "td" || tag === "th") {
    for (const name of ["colspan", "rowspan"]) {
      const v = readAttr(rawAttrs, name);
      if (v != null && /^\d+$/.test(v.trim())) {
        parts.push(`${name}="${escapeAttr(v.trim())}"`);
      }
    }
  }
  return parts.length ? " " + parts.join(" ") : "";
}

/** Escapes a string for safe inclusion inside a double-quoted attribute. */
function escapeAttr(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/** Decodes the small set of HTML entities used when input is double-escaped. */
function decodeEntities(s: string): string {
  return s
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#0*39;/gi, "'")
    .replace(/&apos;/gi, "'")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&");
}

/**
 * Sanitises legacy editorial body HTML to a safe rich whitelist.
 *
 * @param html Raw body HTML (may be double-escaped).
 * @returns Sanitised HTML safe to inject via `dangerouslySetInnerHTML`.
 */
export function sanitizeRichTextHtml(html: string): string {
  if (typeof html !== "string" || !html.trim()) return "";

  let out = html;

  // Double-escaped input (`&lt;p&gt;…`) — decode once so the tag pass can run.
  // Detected when there are no real tags but escaped tag markers are present.
  if (!/<[a-z!/]/i.test(out) && /&lt;\s*\/?[a-z]/i.test(out)) {
    out = decodeEntities(out);
  }

  // Strip Drupal/PHP processing fragments and HTML comments entirely.
  out = out.replace(/<\?[\s\S]*?\?>/g, "");
  out = out.replace(/<!--[\s\S]*?-->/g, "");
  out = out.replace(/<!\[CDATA\[[\s\S]*?\]\]>/g, "");

  // Drop dangerous elements together with their content.
  for (const tag of DROP_CONTENT_TAGS) {
    const re = new RegExp(`<${tag}\\b[\\s\\S]*?<\\/${tag}\\s*>`, "gi");
    out = out.replace(re, "");
    // Also drop a stray self-closing / unclosed opening tag.
    out = out.replace(new RegExp(`<${tag}\\b[^>]*>`, "gi"), "");
  }

  // Process every remaining tag: keep whitelisted ones with sanitised
  // attributes, strip the markup of everything else (content is kept).
  out = out.replace(
    /<\s*(\/?)\s*([a-zA-Z][a-zA-Z0-9]*)((?:[^>"']|"[^"]*"|'[^']*')*)\s*(\/?)\s*>/g,
    (_match, closing: string, rawTag: string, rawAttrs: string) => {
      const tag = rawTag.toLowerCase();
      if (!ALLOWED_TAGS.has(tag)) return "";
      if (closing) {
        return VOID_TAGS.has(tag) ? "" : `</${tag}>`;
      }
      // Drop `<img>` tags whose `src` is empty, missing, or points at a
      // legacy Drupal upload path that was never migrated to the new
      // resource bucket (`/sites/default/files/...`). Those produce
      // broken-image icons on every editorial page that survived from
      // the legacy site without its inline images.
      if (tag === "img" && !isUsableImageSrc(readAttr(rawAttrs, "src"))) {
        return "";
      }
      const attrs = safeAttributes(tag, rawAttrs);
      if (VOID_TAGS.has(tag)) return `<${tag}${attrs}>`;
      return `<${tag}${attrs}>`;
    },
  );

  // Strip any remaining `<figure>…</figure>` shells that wrapped the now-removed
  // image and are left visually empty (no text inside).
  out = out.replace(/<figure\b[^>]*>\s*<\/figure>/gi, "");

  // Strip empty inline shells (`<i></i>`, `<span></span>`, `<b></b>`, `<u></u>`,
  // `<em></em>`, `<strong></strong>`) left behind once Font-Awesome / Bootstrap
  // class attributes were stripped by the attribute whitelist above. Several
  // passes catch nested cases (e.g. `<div><span><i></i></span></div>`).
  // We deliberately do NOT touch `<p>`/`<div>` here — see the next pass.
  for (let i = 0; i < 3; i++) {
    out = out.replace(
      /<(i|span|b|u|em|strong)\b[^>]*>(?:\s|&nbsp;)*<\/\1\s*>/gi,
      "",
    );
  }

  // Strip empty container blocks (`<div></div>`, `<p></p>`) that the source
  // used as visual columns (Drupal `col-lg-*`) but became empty once the
  // class attribute + their only child (an empty `<img>`) were dropped.
  // Multi-pass: a div that becomes empty after its children are stripped
  // should itself be dropped on the next pass.
  for (let i = 0; i < 4; i++) {
    out = out.replace(
      /<(div|p)\b[^>]*>(?:\s|&nbsp;)*<\/\1\s*>/gi,
      "",
    );
  }

  return out.trim();
}

/**
 * True when an `<img src>` is something the browser can actually load:
 *  - non-empty
 *  - not a legacy unmigrated Drupal upload path (`/sites/default/files/…`)
 *
 * The first check catches every `<img src="">` left behind when an
 * editorial page was migrated without its inline images; the second
 * suppresses broken-icon renders on pages that still reference the
 * pre-migration `sites/default/files` tree.
 */
function isUsableImageSrc(src: string | null): boolean {
  if (src == null) return false;
  const trimmed = src.trim();
  if (!trimmed) return false;
  if (/^\/?sites\/default\/files\//i.test(trimmed)) return false;
  return true;
}

/** Returns true when the string carries HTML markup. */
export function isRichTextHtml(s: string): boolean {
  return typeof s === "string" && /<[a-z]/i.test(s);
}
